import { useTranslation } from 'react-i18next';
import { eurosMWp, eurosMW, europeanFormatNumber } from '@/helpers';
import { useBooleanDisplayNormalizer, useConnectionTo, useConnectionType } from '@/hooks';
import { Button, Icon, Typography } from '@/components';

export type ConnectionInfraBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function ConnectionInfrastructureBox({ normalizedProject, isEditable, onEdit }: ConnectionInfraBoxProps) {
  const { t } = useTranslation(['projects']);
  const connection = normalizedProject.connectionInfrastructure?.[0];
  const technology = normalizedProject.technologies;
  const suffix = technology.some((t: any) => t.type === 'solar') ? eurosMWp : eurosMW;
  const isBiogas = technology.some((t: any) => t.type === 'biogas');
  const booleanFormatter = useBooleanDisplayNormalizer();

  const { map: mapConnectionTo } = useConnectionTo();

  const { map: mapConnectionType } = useConnectionType();

  if (!isEditable && connection === undefined) return null;

  const data = [
    {
      label: t('Evacuation line distance'),
      value:
        connection?.evacuation_line_total_distance && connection.evacuation_line_total_distance !== 0
          ? `${europeanFormatNumber(connection.evacuation_line_total_distance, 0)} km`
          : null,
      showWhen: !isBiogas,
    },
    {
      label: t('Evacuation line voltage'),
      value:
        connection?.evacuation_line_voltage && connection.evacuation_line_voltage !== 0
          ? `${europeanFormatNumber(connection.evacuation_line_voltage, 0)} kV`
          : null,
      showWhen: !isBiogas,
    },
    {
      label: t('Connection type'),
      value: connection?.connection_type?.map(mapConnectionType).join(', '),
      showWhen: !isBiogas,
    },
    {
      label: t('Connection to'),
      value: connection?.connection_to?.map(mapConnectionTo).join(', '),
      showWhen: !isBiogas,
    },
    {
      label: t('Line estimated Costs'),
      value: suffix(connection?.line_estimated_costs),
    },
    {
      label: t('Grid Conditions Costs'),
      value:
        connection?.technical_and_economical_conditions_costs &&
        connection?.technical_and_economical_conditions_costs !== 0
          ? suffix(connection?.technical_and_economical_conditions_costs)
          : null,
    },
    {
      label: t('Connection Costs Comments'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">
          {connection?.connection_costs_comments}
        </span>
      ),
    },
    {
      label: t('Shared Infrastructure'),
      value: booleanFormatter(connection?.shared_infrastructure),
    },
    ...(connection?.shared_infrastructure
      ? [
          {
            label: t('Shared Infrastructure Comments'),
            value: (
              <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">
                {connection?.shared_infrastructure_comments}
              </span>
            ),
          },
        ]
      : []),
    {
      label: t('Other Connection Infrastructure Comments'),
      value: (
        <span className="text-base font-normal whitespace-pre-wrap text-neutral-900">
          {connection?.connection_infrastructure_comments}
        </span>
      ),
    },
  ];

  const biogasData = [
    {
      label: t('Gas Grid Connection Pressure'),
      value:
        connection?.gas_grid_connection_pressure && connection.gas_grid_connection_pressure !== 0
          ? `${europeanFormatNumber(connection.gas_grid_connection_pressure, 0)} bar`
          : null,
    },
    {
      label: t('Distance to Gas Injection Point'),
      value:
        connection?.distance_to_gas_injection_point && connection.distance_to_gas_injection_point !== 0
          ? `${europeanFormatNumber(connection.distance_to_gas_injection_point, 0)} km`
          : null,
    },
  ];

  const finalData = isBiogas ? [...biogasData, ...data.filter((d) => d.showWhen !== false)] : data;

  const visibleData = isEditable
    ? finalData
    : finalData.filter(({ value }) => value !== null && value !== undefined && value !== '');

  if (!isEditable && visibleData.length === 0) return null;

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="capacity-energy" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Connection Infrastructure')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleData.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>
    </div>
  );
}
